import { Link } from "react-router-dom";

import { cn } from "@shared/lib/utils";
import { Button } from "@shared/ui/button";
import { Heading } from "@shared/ui/typography";

import type { LandingProductData } from "./LandingProductsGrid";
import { LandingProductVideo } from "./LandingProductVideo";

type LandingProductsGridItemProps = LandingProductData;

export const LandingProductsGridItem = ({
  variant,
  title,
  subtitle,
  description,
  badges,
  enabled = true,
  comingSoon = false,
  ctaLabel,
  ctaTo,
  image,
  video,
}: LandingProductsGridItemProps) => {
  const showCta = enabled && !comingSoon;
  const isLarge = variant === "large";

  const variantWrapperClass = isLarge
    ? "col-span-3 pb-4 sm:pb-6 xl:pb-0"
    : "col-span-3 xl:col-span-1 sm:max-xl:not-last:border-b border-border py-6 sm:py-8 md:py-10 xl:pt-0";
  const variantItemClass = isLarge
    ? "flex-col gap-6"
    : "flex-col gap-5 sm:gap-8 md:max-xl:gap-12 sm:max-xl:flex-row sm:max-xl:group-odd/product:flex-row-reverse sm:max-xl:items-center xl:flex-col-reverse";
  const variantLayoutClass = isLarge
    ? "grid items-baseline gap-y-3 md:gap-x-8 md:gap-y-6 pb-2 md:pb-6 lg:grid-cols-2 lg:gap-x-10"
    : "flex flex-col gap-1.5 grow min-w-0 sm:basis-0 xl:basis-auto";
  const variantVisualClass = isLarge
    ? "xl:aspect-video"
    : "sm:basis-0 sm:grow xl:basis-auto xl:grow-0";
  const variantParagraphClass = isLarge
    ? "text-foreground-2 text-base sm:text-lg md:text-xl xl:text-2xl"
    : "text-foreground-4 grow text-sm lg:text-base mb-2 md:mb-4";

  return (
    <li className={cn(`group/product flex`, variantWrapperClass)}>
      <article className={cn(`flex grow`, variantItemClass)}>
        <div className={cn("relative", variantLayoutClass)}>
          <Heading tag="h3" size={isLarge ? "4xl" : "3xl"}>
            {title}
            {isLarge && <span>: {subtitle}</span>}
          </Heading>

          <div className="relative row-span-2 flex grow flex-col gap-4 pb-2">
            {!isLarge && (
              <p
                className="
                  text-base/5.5 font-medium tracking-tight text-pretty text-foreground-2
                  lg:max-w-[30ch] lg:text-lg/6
                  xl:text-xl/6.5
                "
              >
                {subtitle}
              </p>
            )}

            <p className={variantParagraphClass}>{description}</p>
            <div
              className="
                flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs/4 tracking-wide
                text-foreground-5 uppercase
                lg:gap-x-6
              "
            >
              {badges.map((badge, index) => (
                <span
                  key={`${badge}-${String(index)}`}
                  className="inline-flex items-center"
                >
                  <span>{badge}</span>
                </span>
              ))}
            </div>
          </div>

          {showCta ? (
            <div>
              <Link to={ctaTo}>
                <Button size="xl">{ctaLabel}</Button>
              </Link>
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            `
              group/product-visual relative flex aspect-3/2 size-auto items-center justify-center
              overflow-hidden rounded-2xl bg-background-1
              max-sm:-mx-2
            `,
            variantVisualClass,
          )}
        >
          {comingSoon && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <div
                className="
                  flex w-fit origin-center rounded-full bg-background-3 px-3 py-1.5 text-sm/5
                  font-medium text-foreground-5 shadow-sm outline outline-border transition
                  duration-400 ease-out
                  group-hover/product-visual:scale-110 group-hover/product-visual:shadow-lg
                  group-hover/product-visual:outline-input
                "
              >
                Coming soon
              </div>
            </div>
          )}
          {video ? (
            <LandingProductVideo title={title} video={video} />
          ) : image ? (
            <img
              src={image}
              alt=""
              className="
                size-full object-cover transition-[filter] duration-500 ease-[ease]
                group-hover/product-visual:blur-sm
              "
              loading="lazy"
              decoding="async"
              aria-hidden="true"
            />
          ) : null}
        </div>
      </article>
    </li>
  );
};
